import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PeekStack } from "@piiqtechnologies/ui-components";
import { CustomSubtopicCard } from "../../custom-subtopics-card";
import messages from "./messages.i18n";
const CARDS = [
    {
        key: "studio",
        titleMessage: "studioTitle",
        subtitleMessage: "studioSubtitle",
        tooltipMessage: "studioTooltip",
    },
    {
        key: "oneBedroom",
        titleMessage: "oneBedroomTitle",
        subtitleMessage: "oneBedroomSubtitle",
        tooltipMessage: "oneBedroomTooltip",
    },
    {
        key: "twoBedrooms",
        titleMessage: "twoBedroomsTitle",
        subtitleMessage: "twoBedroomsSubtitle",
        tooltipMessage: "twoBedroomsTooltip",
    },
    {
        key: "threeBedrooms",
        titleMessage: "threeBedroomsTitle",
        subtitleMessage: "threeBedroomsSubtitle",
        tooltipMessage: "threeBedroomsTooltip",
    },
];
export const PriceLayoutTabContent = ({ maxAddedSubtopics, otherTabsAddedCount = 0, baseSubtopicsData, onAddedCountChange, onSubtopicsDataChange, saveTrigger, onEditingCountChange, initialData, readOnly, hasBeenRun, }) => {
    const intl = useIntl();
    const addedCountsRef = useRef({});
    const subtopicsDataRef = useRef({});
    const editingCountsRef = useRef({});
    const [totalAdded, setTotalAdded] = useState(0);
    const [totalEditing, setTotalEditing] = useState(0);
    // Toggle temporarily hidden — force every card to "active" so getOverrideData passes the
    // real subtopics through instead of nulling them out (which would trigger BE removal).
    const [activeStates, setActiveStates] = useState(() => CARDS.reduce((acc, card) => (Object.assign(Object.assign({}, acc), { [card.key]: true })), {}));
    const activeStatesRef = useRef(activeStates);
    activeStatesRef.current = activeStates;
    const createAddedCountHandler = useCallback((cardKey) => (count) => {
        addedCountsRef.current[cardKey] = count;
        const newTotal = Object.values(addedCountsRef.current).reduce((sum, c) => sum + c, 0);
        setTotalAdded(newTotal);
    }, []);
    const getOverrideData = useCallback(() => {
        const data = {};
        for (const key of Object.keys(subtopicsDataRef.current)) {
            data[key] = activeStatesRef.current[key] ? subtopicsDataRef.current[key] : null;
        }
        return data;
    }, []);
    const createSubtopicsChangeHandler = useCallback((cardKey) => (subtopics) => {
        subtopicsDataRef.current[cardKey] = subtopics;
        onSubtopicsDataChange === null || onSubtopicsDataChange === void 0 ? void 0 : onSubtopicsDataChange(getOverrideData());
    }, [onSubtopicsDataChange, getOverrideData]);
    const createToggleHandler = useCallback((cardKey) => (active) => {
        setActiveStates((prev) => (Object.assign(Object.assign({}, prev), { [cardKey]: active })));
        activeStatesRef.current = Object.assign(Object.assign({}, activeStatesRef.current), { [cardKey]: active });
        onSubtopicsDataChange === null || onSubtopicsDataChange === void 0 ? void 0 : onSubtopicsDataChange(getOverrideData());
    }, [onSubtopicsDataChange, getOverrideData]);
    const createEditingCountHandler = useCallback((cardKey) => (count) => {
        editingCountsRef.current[cardKey] = count;
        const newTotal = Object.values(editingCountsRef.current).reduce((sum, c) => sum + c, 0);
        setTotalEditing(newTotal);
    }, []);
    const cardHandlers = useMemo(() => CARDS.reduce((acc, card) => (Object.assign(Object.assign({}, acc), { [card.key]: {
            addedCount: createAddedCountHandler(card.key),
            subtopics: createSubtopicsChangeHandler(card.key),
            editing: createEditingCountHandler(card.key),
            toggle: createToggleHandler(card.key),
        } })), {}), [createAddedCountHandler, createSubtopicsChangeHandler, createEditingCountHandler, createToggleHandler]);
    useEffect(() => {
        onAddedCountChange === null || onAddedCountChange === void 0 ? void 0 : onAddedCountChange(totalAdded);
    }, [totalAdded, onAddedCountChange]);
    useEffect(() => {
        onEditingCountChange === null || onEditingCountChange === void 0 ? void 0 : onEditingCountChange(totalEditing);
    }, [totalEditing, onEditingCountChange]);
    const canAddMore = totalAdded + otherTabsAddedCount < maxAddedSubtopics;
    return (React.createElement(PeekStack, { direction: "row", gap: 1.5 }, CARDS.map((card) => {
        var _a, _b, _c, _d;
        return (React.createElement(CustomSubtopicCard, { key: card.key, variant: "price-layout", title: intl.formatMessage(messages[card.titleMessage]), subtitle: intl.formatMessage(messages[card.subtitleMessage]), tooltip: intl.formatMessage(messages[card.tooltipMessage]), initialSubtopics: (_a = initialData === null || initialData === void 0 ? void 0 : initialData[card.key]) !== null && _a !== void 0 ? _a : [], baseCount: (_c = (_b = baseSubtopicsData === null || baseSubtopicsData === void 0 ? void 0 : baseSubtopicsData[card.key]) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0, originalSubtopics: (_d = baseSubtopicsData === null || baseSubtopicsData === void 0 ? void 0 : baseSubtopicsData[card.key]) !== null && _d !== void 0 ? _d : undefined, canAddMore: canAddMore, maxSubtopics: maxAddedSubtopics, onAddedCountChange: cardHandlers[card.key].addedCount, onSubtopicsChange: cardHandlers[card.key].subtopics, saveTrigger: saveTrigger, onEditingCountChange: cardHandlers[card.key].editing, 
            // Toggle temporarily hidden — keep isToggled forced on so the card renders its
            // subtopics normally instead of the "omitted" placeholder state.
            isToggled: true, 
            // showToggle
            // onToggle={cardHandlers[card.key].toggle}
            // toggleTooltip={intl.formatMessage(messages.hideSubtopicTooltip)}
            readOnly: readOnly, hasBeenRun: hasBeenRun }));
    })));
};
